import Vue from 'vue'
import moment from 'moment'
import { deepFreeze } from '@/functions/deepFreeze'

const mutations = {
    // TRUCKS
    setTrucks(state, trucks) {
        state.trucks = [...trucks]
    },
    setTrailerHooked(state, trucksHooked) {
        state.truck_hooked_list = [...trucksHooked]
    },
    // REFACTOR-009: rimossa mutation setTrucksRef. Il lookup id→truck è ora
    // derivato dal getter `trucksMap` (vedi geofleet.module.js), eliminato
    // il doppio storage che richiedeva sincronizzazione manuale dai bootstrap.
    setFocusTruck(state, payload) {
        state.trucks = state.trucks.map((t) => ({
            ...t,
            on_focus: t.id === payload.truck.id ? payload.on_focus : false
        }))
    },
    // REFACTOR-007 (Sprint 7): foundation del bus di focus imperativo.
    // Vedi state.focusRequest in geofleet.module.js. Nuova reference a ogni commit
    // → trigger garantito dei watcher in GeofleetMap (setView) e MainPanel (scroll).
    // payload: { truck, fromMapMarker }.
    requestTruckFocus(state, payload) {
        state.focusRequest = { truck: payload.truck, fromMapMarker: !!payload.fromMapMarker }
    },
    // Bus for the /location route. LocationPage commits the resolved address;
    // GeofleetMap watches and populates its search bar. Always assign a fresh
    // object so the watcher fires for identical coords on repeat navigations.
    setLocationRequest(state, payload) {
        state.locationRequest = payload
            ? { lat: payload.lat, lng: payload.lng, label: payload.label }
            : null
    },
    setActivityTruck(state, payload) {
        state.trucks = state.trucks.map((t) => ({
            ...t,
            on_activity: t.id === payload.truck.id ? payload.on_activity : false
        }))
    },
    updateTruck(state, payload) {
        let index = state.trucks.findIndex((t) => t.id === payload.truck.id)
        if (index !== -1) {
            state.trucks = [
                ...state.trucks.slice(0, index),
                { ...payload.truck },
                ...state.trucks.slice(index + 1)
            ]
        }
    },
    // Append di un truck "nuovo" (non ancora in state.trucks). Idempotente:
    // se l'id esiste già, no-op. Usata dal recovery in updateData. Vedi BUG-006.
    // BUG-016: clone shallow del payload prima dello store push — evita reference
    // aliasing con `trucks_ref` del componente chiamante (mutazioni dirette su
    // trucks_ref[v.id] altrimenti alterano state.trucks bypassando le mutation,
    // violando il flow Vuex unidirezionale).
    appendTruck(state, truck) {
        if (!truck || truck.id == null) return
        if (state.trucks.some((t) => t.id === truck.id)) return
        state.trucks = [...state.trucks, { ...truck }]
    },
    // Batch update: un solo trigger reattivo per N veicoli aggiornati. Vedi REFACTOR-011.
    // delta = [{ truck: {...} }, ...]. Se lo stesso id appare più volte vince l'ultimo.
    //
    // BUG-013: SEMANTICA "update-only, mai append" — patch per id NON in state.trucks
    // sono droppate silenziosamente. Per truck nuovi usare commit('appendTruck', truck).
    // Adesso `console.warn` segnala i drop per debug (race tra appendTruck e polling, o
    // future call site che assumono upsert).
    //
    // REFACTOR-019: trade-off complessità O(N) anche per delta piccoli.
    // patchMap.get() è O(1) ma l'outer state.trucks.map(...) è O(N) (N = totale truck
    // in state). Su flotta 1000 con delta=5, si itera 1000 truck + 1 alloc array da 1000
    // ad ogni ciclo polling 20s. Tradeoff scelta: .map() è la via più semplice per
    // dare a Vue 2 una clean reactive trigger sull'array (re-rendering completo OK).
    // Alternative valutate:
    //   - splice mirato per index → O(K) con K=delta size, ma fragile (Vue 2 deve
    //     osservare le mutazioni splice + indici devono restare validi).
    //   - state.trucks come Map → cambio interfaccia consumer, scope ben oltre 5.5.
    // Soglia di valutazione ottimizzazione: se profiling mostra che .map() pesa
    // su flotta >2000 truck con delta <2% del fleet, valutare splice mirato.
    mergeTrucksDelta(state, delta) {
        if (!delta || delta.length === 0) return

        const patchMap = new Map()
        delta.forEach((entry) => {
            const truck = entry && entry.truck
            if (truck && truck.id != null) patchMap.set(truck.id, truck)
        })

        if (patchMap.size === 0) return

        const knownIds = new Set(state.trucks.map((t) => t.id))
        const droppedIds = []
        patchMap.forEach((_patch, id) => {
            if (!knownIds.has(id)) droppedIds.push(id)
        })
        if (droppedIds.length > 0) {
            console.warn(
                'BUG-013 mergeTrucksDelta: dropping patches for ids not in state.trucks (use appendTruck for new trucks)',
                { droppedIds }
            )
        }

        state.trucks = state.trucks.map((t) => {
            const patch = patchMap.get(t.id)
            return patch ? { ...patch } : t
        })
    },
    updateTruckProperty(state, payload) {
        let index = state.trucks.findIndex((t) => t.id === payload.truck.id)
        if (index !== -1) {
            state.trucks = [
                ...state.trucks.slice(0, index),
                {
                    ...state.trucks[index],
                    [payload.property]: payload.value
                },
                ...state.trucks.slice(index + 1)
            ]
        }
    },
    setTrucksEvents(state, events) {
        const specialVehicleEvents = [
            {
                id: 18,
                event_type: 'door_open',
                position: ['45.4383842', '10.9916215'],
                truck_id: 144101
            },
            {
                id: 19,
                event_type: 'engine_on',
                position: ['45.4384023', '10.9916124'],
                truck_id: 144101
            },
            {
                id: 20,
                event_type: 'engine_off',
                position: ['45.4384204', '10.9916033'],
                truck_id: 144101
            }
        ]
        state.trucksEvents = [...events, ...specialVehicleEvents]
    },
    setTrucksLastEventId(state, id) {
        state.trucksLastEventId = id
    },

    // BLUETOOTH DEVICES
    setBluetoothDevices(state, devices) {
        state.bluetooth_devices = [...devices]
    },

    // MAIN MAP
    setMainMapStopAutocenter(state, value) {
        state.mainMapStopAutocenter = value
    },
    setMainMapStopAutoFitBounds(state, value) {
        state.mainMapStopAutoFitBounds = value
    },
    setMainMapInstance(state, map) {
        state.mainMapInstance = map
    },
    addMainMapExtraObject(state, payload) {
        const { object, type } = payload
        const ref = object.ref
        const index = state.mainMapExtraObjects[type].findIndex((o) => o.ref === ref)

        state.mainMapExtraObjects = {
            ...state.mainMapExtraObjects,
            [type]:
                index !== -1
                    ? [
                          ...state.mainMapExtraObjects[type].slice(0, index),
                          { ...object },
                          ...state.mainMapExtraObjects[type].slice(index + 1)
                      ]
                    : [...state.mainMapExtraObjects[type], { ...object }]
        }
    },
    removeMainMapExtraObject(state, payload) {
        const { object, type } = payload
        const index = state.mainMapExtraObjects[type].indexOf(object)

        if (index !== -1) {
            state.mainMapExtraObjects = {
                ...state.mainMapExtraObjects,
                [type]: [
                    ...state.mainMapExtraObjects[type].slice(0, index),
                    ...state.mainMapExtraObjects[type].slice(index + 1)
                ]
            }
        }
    },

    // VEHICLES CROSS
    setVehiclesCrossSearch(state, search) {
        state.vehiclesCrossSearch = { ...search }
    },
    resetVehiclesCrossSearch(state) {
        state.vehiclesCrossSearch = {}
    },
    setVehiclesCrossSeed(state, seed) {
        state.vehiclesCrossSeed = seed
    },
    clearVehiclesCrossSeed(state) {
        state.vehiclesCrossSeed = null
    },
    setNearVehiclesSeed(state, seed) {
        state.nearVehiclesSeed = seed
    },
    clearNearVehiclesSeed(state) {
        state.nearVehiclesSeed = null
    },
    setVehiclesCrossResults(state, results) {
        state.vehiclesCrossResults = results.map((v) => ({
            ...v,
            grouped_events: v.events.reduce((acc, event, i) => {
                if (i % 2 === 0) {
                    const next_event = v.events[i + 1] || null
                    acc.push({
                        start_timestamp: event.timestamp?.$date?.$numberLong,
                        end_timestamp: next_event?.timestamp?.$date?.$numberLong || null,
                        event,
                        next_event
                    })
                }
                return acc
            }, [])
        }))
    },
    resetVehiclesCrossResults(state) {
        state.vehiclesCrossResults = []
    },

    // DRIVERS DATA
    setDrivers(state, drivers) {
        // deep-frozen: collezione read-only post-bootstrap. Vedi REFACTOR-008.
        state.drivers = deepFreeze({ ...drivers })
    },
    setDriversData(state, driversData) {
        state.driversData = { ...driversData }
    },
    updateDriversData(state, payload) {
        state.driversData = {
            ...state.driversData,
            ...payload
        }
    },

    // COMPANIES
    setCompanies(state, companies) {
        // deep-frozen: collezione read-only post-bootstrap. Vedi REFACTOR-008.
        state.companies = deepFreeze({ ...companies })
    },

    //PLAFONDS DRIVER
    setDriversPlafondData(state, { id, data }) {
        state.driversPlafondData = {
            ...state.driversPlafondData,
            [id]: {
                ...data,
                __cacheTime: moment()
            }
        }
    },
    cleanDriversPlafondCache(state) {
        state.driversPlafondData = {}
    },
    removeDriverFromDriversPlafondData(state, id) {
        const { [id]: removed, ...rest } = state.driversPlafondData
        state.driversPlafondData = rest
    },

    // GMAPS
    setGmaps(state, gmaps) {
        // Keep the original Google Places namespace object reference.
        // Spreading may drop non-enumerable/prototype members used as constructors.
        state.gmaps = gmaps || null
    },

    // COMPANY TAGS
    setCompanyTags(state, tags) {
        state.companyTags = [...tags]
    },
    addCompanyTag(state, tag) {
        state.companyTags = [...state.companyTags, tag]
    },
    updateCompanyTag(state, tag) {
        const index = state.companyTags.findIndex((t) => t.id === tag.id)
        if (index !== -1) {
            state.companyTags = [
                ...state.companyTags.slice(0, index),
                { ...tag },
                ...state.companyTags.slice(index + 1)
            ]
        }
    },
    removeCompanyTag(state, tag) {
        state.companyTags = state.companyTags.filter((t) => t.id !== tag.id)
    },

    // FILTERS DICTIONARY
    setFiltersDictionary(state, filters) {
        state.filtersDictionary = { ...filters }
    },
    setStationsFiltersDictionary(state, filters) {
        state.filtersDictionary = {
            ...state.filtersDictionary,
            stations: filters
        }
    },
    setParkingsFiltersDictionary(state, filters) {
        state.filtersDictionary = {
            ...state.filtersDictionary,
            parkings: filters
        }
    },

    // CONFIG — migrato al modulo namespaced `config` (epic REFACTOR-030, Sprint 16).
    // setConfig/setCompanyConfig/setConfigProperty/resetConfig rimossi: lo stato e
    // le mutation config vivono ora in src/store/config/. Vedi config-bus.md.
    setTruckFilteredOut(state, ids) {
        state.truckFilteredOut = ids ? [...ids] : []
    }
}

export default mutations
